
import {ProjectElement}                    from '@/models/project/project.element';

import {Project}                           from '@/models/project';
import {Logger}                            from "@/util/logger";
import {LocomotiveDialogParams}            from "@/gui/dialogs/locomotive.dialog.params";
import {fromMySQLDate}                     from "@/util/util";
import {LocoQueryService}                  from "@/services/loco.query.service";
import {LocomotiveDataHandle}              from "@/models/project/locomotive.data.handle";
import {ProjectElementFactory}             from "@/models/project.element.factory";
import {ProjectElementType}                from "@/models/project/project.element.type";
import {TaskUpdate}                        from "@/models/task.update";
import {TaskLocomotiveModelCreate}         from "@/models/task.locomotive.model.create";
import {TaskProgressService}               from "@/services/task.progress.service";
import {TaskLocomotiveModelCreateFailed}   from "@/models/task.locomotive.model.create.failed";
import {TaskLocomotiveModelCreateComplete} from "@/models/task.locomotive.model.create.complete";

/**
 *
 * @class LocomotiveModelCreator
 *
 * helper to take the parameters from a locomotive creation dialog and do
 * all the necessary work to actually create the model and then add it as a leaf to
 * the locomotive models branch in the user's current project.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
export class LocomotiveModelCreator {

  /** the user's current project */
  private _project: Project;

  /** standard constructor */
  constructor(
    project: Project
  ) {

    this._project                   = project;
    let lmc: LocomotiveModelCreator = this;

    Logger.debug('[LocomotiveModelCreator] adding new Locomotive Model Group...');

    /* go through a dialog to get the name and parameters for the model */

    lmc._project.services.modals.open(
      'new-loco-modal',
      (value: LocomotiveDialogParams, resolve, reject) => {

        let err: string = lmc.validate(value);

        if (err != '') {

          reject(err);
        }

        resolve(value);
      },
      (value: LocomotiveDialogParams) => {

        lmc.create(value);

      });

  }

  /** the dialog parameters wer accepted, time to create a model! */
  create(value: LocomotiveDialogParams) {

    let lmc: LocomotiveModelCreator = this;
    let loco: LocoQueryService      = lmc._project.services.locoquery;

    /* who is the user? */

    let userid: string = lmc._project.services.auth.userOfSession.username;

    /* if they gave the dates backwards, reverse them */

    let sd: Date = fromMySQLDate(value.start);
    let ed: Date = fromMySQLDate(value.end);

    if (ed.getTime() < sd.getTime()) {

      /* they are backwards */

      Logger.debug(`[LocomotiveModelCreator] create - start/end are backwards, flipping...`);

      let tmp: string = value.start;
      value.start     = value.end;
      value.end       = tmp;

    }

    /* add the new child model */

    Logger.debug(`[LocomotiveModelCreator] create - new locomotive model (${userid}): `, value);

    const tasks: TaskProgressService = lmc._project.services.taskMonitor;
    const taskId: string = TaskUpdate.generateId();

    tasks.progress(new TaskLocomotiveModelCreate(taskId, `Creating (might take a minute): ${value.name} ...`, 1.0));

    /* start the creation of the model... */

    loco.locoCreate(value.fav.dataset_name, value.start, value.end).then(
      (createHandle: LocomotiveDataHandle) => {

        Logger.debug('[LocomotiveModelCreator] create - model created: ', createHandle);

        /* track the creation */

        tasks.progress(new TaskLocomotiveModelCreate(taskId, `Calculating (might take a minute)...`, 50.0));

        /* with the model created, we can now do the calculation step... */

        loco.locoCalculate(createHandle.id).then(
          (calculateHandle: LocomotiveDataHandle) => {

            /* track calculation complete */

            tasks.progress(new TaskLocomotiveModelCreate(taskId, `Model calculated, saving...`, 99.0));

            /* the model is ready for use, It's time to add the result node to the project tree. */

            /* add the new locomotive model in the locomotive models' section of the project... */

            Logger.debug('[LocomotiveModelCreator] new loco model: ',  calculateHandle);

            let parent: ProjectElement = lmc._project.locomotiveModels();

            ProjectElementFactory.fromObject(ProjectElementType.LocoMotiveModelResult, {
              project:  lmc._project,
              handle:   calculateHandle,
              fav:      value.fav,
              name:     value.name,
              start:    value.start,
              end:      value.end,
              parent,
              publish:  true
            });

            /* make sure the thing we just created is visible */

            lmc._project.expand([parent]);

            /* make sure the project is saved */

            parent.save();

            /* track success */

            tasks.progress(new TaskLocomotiveModelCreateComplete(taskId, `[${value.name}] Model ready!`));

          },
          (error) => {

            /* track the error */

            tasks.progress(new TaskLocomotiveModelCreateFailed(taskId, error));

            /* log it */

            Logger.debug('[LocomotiveModelCreator] create - could not do first time calculation model: ', error);

          });

      },
      (error) => {

        /* track the error */

        tasks.progress(new TaskLocomotiveModelCreateFailed(taskId, error));

        /* log it */

        Logger.debug('[LocomotiveModelCreator] create - could not create model: ', error);

      });
  }

  /** validate the parameters we got from the dialog */
  validate(value: LocomotiveDialogParams): string {

      let lmc: LocomotiveModelCreator = this;
      let parent: ProjectElement      = lmc._project.locomotiveModels();
      let name: string                = value.name;

      Logger.debug('[LocomotiveModelCreator] (new loco) validating: ', name);

      /* is it a valid name? */

      if (name.trim() === '') {

        return 'Model name can not be empty.';
      }

      /* do we already have a model of this name? */

      const peers: ProjectElement[] = parent._children;

      for (const peer of peers) {

        if (peer._label === name) {

          return 'Model of that name already exists in project.';
        }

      }

      if (value.fav === null) {

        return 'Must select a FAV';
      }

      if (value.start === '') {

        return 'Must set start date';
      }

      if (value.end === '') {

        return 'Must set end date';
      }

      /* its golden! */

      value.name = name;

      return '';
    }

}
